import { useEffect, useMemo, useRef, useState } from "react";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { ChevronDown, Eye } from "lucide-react";

// Admin-only "View as principal" selector.
// While active the admin sees the principal's scoped dataset AND edits on
// their behalf (portfolio writes carry ?as=<id>; firm-wide actions blocked).
// Fetches the full principal list (unscoped /api/data) once on mount so the
// dropdown still works even while currently viewing as a specific principal.
export default function ViewAsSelector() {
  const { viewAs, setViewAs } = useData();
  const [open, setOpen] = useState(false);
  const [principals, setPrincipals] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    // Load the firm-wide principal list once.
    api.get("/data").then((res) => {
      setPrincipals(res.data.principals || []);
    }).catch((error) => {
      // Non-admin, view-as denied, or transient — the selector just stays
      // empty. Log so a real outage doesn't disappear silently.
      console.error("ViewAsSelector · /data fetch failed", error);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = useMemo(
    () => principals.find((p) => p.id === viewAs) || null,
    [principals, viewAs]
  );

  const label = active ? active.name.split(/\s+/)[0] : "Firm";

  return (
    <div ref={ref} className="relative" data-testid="view-as-selector">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 border text-[11px] font-display tracked-tight uppercase ${
          viewAs ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--rule-strong)] bg-white text-[var(--ink)] hover:bg-[var(--paper-soft)]"
        }`}
        data-testid="view-as-button"
        title="View & edit as a principal"
      >
        <Eye size={11} />
        <span className="eyebrow normal-case tracking-normal opacity-70">View as</span>
        <span className="font-mono-num normal-case tracking-normal">{label}</span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-60 bg-white border border-[var(--rule-strong)] shadow-sm animate-fade-up">
          <button
            onClick={() => { setViewAs(null); setOpen(false); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--paper-soft)] ${!viewAs ? "bg-[var(--paper-soft)] text-[var(--ink)]" : ""}`}
            data-testid="view-as-firm"
          >
            <div className="font-display tracked-tight uppercase text-xs">Firm-wide</div>
            <div className="eyebrow normal-case tracking-[0.06em] text-[var(--soft-fg)]">Full dataset (default)</div>
          </button>
          <div className="border-t border-[var(--rule)]" />
          {principals.map((p) => (
            <button
              key={p.id}
              onClick={() => { setViewAs(p.id); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--paper-soft)] ${viewAs === p.id ? "bg-[var(--paper-soft)] text-[var(--ink)]" : ""}`}
              data-testid={`view-as-${p.id}`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
